import type { RequestOptions } from '@@/plugin-request/request';
import type { RequestConfig } from '@umijs/max';
import { history } from '@umijs/max';
import { message } from 'antd';
import {
  clearOAuth2Tokens,
  ensureValidAccessToken,
  getAccessToken,
  redirectToSSO,
} from './services/oauth2';

// 与后端约定的响应数据格式
interface ResponseStructure {
  success: boolean;
  data: any;
  errorCode?: number;
  errorMessage?: string;
}

type NormalizedRequestError = {
  status?: number;
  message: string;
  original: unknown;
};

type RequestErrorLike = {
  name?: string;
  message?: string;
  info?: {
    errorCode?: number;
    errorMessage?: string;
  };
  response?: {
    status?: number;
    data?: any;
  };
  request?: unknown;
  handled?: boolean;
};

const LOGIN_PATH = '/user/login';
const AUTH_ENDPOINTS_SKIP_REFRESH = [
  '/bff/auth/refreshToken',
  '/bff/auth/codeLogin',
  '/bff/auth/logout',
];
const AUTH_ENDPOINTS_SKIP_AUTH_HEADER = [
  '/bff/auth/refreshToken',
  '/bff/auth/codeLogin',
];
const CURRENT_TENANT_UUID_STORAGE_KEY = 'currentTenantUuid';

const getRequestPathname = (url?: string) => {
  if (!url) return '';

  try {
    const base =
      typeof window !== 'undefined'
        ? window.location.origin
        : 'http://localhost';
    return new URL(url, base).pathname;
  } catch {
    return url;
  }
};

const matchesAuthEndpoint = (url: string | undefined, endpoints: string[]) => {
  const pathname = getRequestPathname(url);
  return endpoints.some((endpoint) => pathname.includes(endpoint));
};

const getTenantUuidFromStorage = () => {
  try {
    return localStorage.getItem(CURRENT_TENANT_UUID_STORAGE_KEY) || undefined;
  } catch {
    return undefined;
  }
};

const getOrganizationUuidFromStorage = () => {
  try {
    const userJson = localStorage.getItem('user');
    if (userJson) {
      const user = JSON.parse(userJson) as API.CurrentUser;
      return (
        user.organizationUuid || user.lastLoginOrganizationUuid || undefined
      );
    }
  } catch {
    // ignore
  }
  return undefined;
};

const handleUnauthorized = () => {
  clearOAuth2Tokens();
  if (
    history.location.pathname !== LOGIN_PATH &&
    history.location.pathname !== '/callback'
  ) {
    redirectToSSO();
  } else if (history.location.pathname !== LOGIN_PATH) {
    history.push(LOGIN_PATH);
  }
};

const extractResponseMessage = (responseData: any): string => {
  if (!responseData) {
    return '';
  }

  if (Array.isArray(responseData.message)) {
    return responseData.message.join(', ');
  }

  return (
    responseData.message ||
    responseData.errorMessage ||
    responseData.error ||
    responseData.msg ||
    ''
  );
};

const normalizeRequestError = (
  error: RequestErrorLike,
): NormalizedRequestError => {
  if (error.name === 'BizError') {
    const bizStatus = error.info?.errorCode;
    return {
      status: bizStatus,
      message: error.info?.errorMessage || error.message || '请求失败',
      original: error,
    };
  }

  if (error.response) {
    const status = error.response.status;
    const responseMessage = extractResponseMessage(error.response.data);
    return {
      status,
      message:
        responseMessage || (status ? `请求失败 (${status})` : '请求失败'),
      original: error,
    };
  }

  return {
    message: error.message || '请求错误，请重试',
    original: error,
  };
};

/**
 * @name 错误处理
 * pro 自带的错误处理， 可以在这里做自己的改动
 * @doc https://umijs.org/docs/max/request#配置
 */
export const errorConfig: RequestConfig = {
  // 错误处理： umi@3 的错误处理方案。
  errorConfig: {
    // 错误抛出
    errorThrower: (res) => {
      const { success, data, errorCode, errorMessage } =
        res as unknown as ResponseStructure;
      if (!success) {
        const error: any = new Error(errorMessage);
        error.name = 'BizError';
        error.info = { errorCode, errorMessage, data };
        throw error; // 抛出自制的错误
      }
    },
    // 错误接收及处理
    errorHandler: (error: RequestErrorLike, opts: any) => {
      if (opts?.skipErrorHandler) throw error;

      const normalized = normalizeRequestError(error);
      const errorCode =
        error.response?.data?.code || (error as any).info?.errorCode;

      if (errorCode === 10010) {
        message.error('租户环境已变更，系统将自动刷新');
        setTimeout(() => {
          localStorage.clear();
          window.location.reload();
        }, 1500);
        throw error;
      }

      if (normalized.status === 401) {
        handleUnauthorized();
        throw error;
      }

      if (error.response) {
        message.error(normalized.message);
      } else if (error.request) {
        // 请求已经成功发起，但没有收到响应
        // \`error.request\` 在浏览器中是 XMLHttpRequest 的实例，
        // 而在node.js中是 http.ClientRequest 的实例
        message.error('网络错误，请检查网络连接后重试');
      } else {
        message.error(normalized.message);
      }

      error.handled = true;
      throw error;
    },
  },

  // 请求拦截器
  requestInterceptors: [
    async (config: RequestOptions) => {
      // 拦截请求配置，进行个性化处理。
      const requestUrl = config?.url as string | undefined;
      const skipTokenRefresh = matchesAuthEndpoint(
        requestUrl,
        AUTH_ENDPOINTS_SKIP_REFRESH,
      );
      const skipAuthHeader = matchesAuthEndpoint(
        requestUrl,
        AUTH_ENDPOINTS_SKIP_AUTH_HEADER,
      );
      if (skipAuthHeader) {
        return config;
      }

      const token = skipTokenRefresh
        ? getAccessToken()
        : await ensureValidAccessToken();
      const tenantUuid = getTenantUuidFromStorage();
      const organizationUuid = getOrganizationUuidFromStorage();

      const headers = { ...config.headers };
      if (tenantUuid) {
        headers['x-tenant-id'] = tenantUuid;
      }
      if (organizationUuid) {
        headers['x-organization-id'] = organizationUuid;
      }
      if (token) {
        headers.Authorization = `Bearer ${token}`;
      }

      config.headers = headers;
      return config;
    },
  ],

  // 响应拦截器
  responseInterceptors: [
    (response) => {
      // 拦截响应数据，进行个性化处理
      const { data } = response as unknown as ResponseStructure;

      if (data?.success === false) {
        message.error('请求失败！');
      }
      return response;
    },
  ],
};
